<template>
  <!-- BUI chrome path (new design). Used when invoked from BuiEntityListingPage. -->
  <div v-if="useBuiChrome" class="bui-lister" @click.stop>
    <div class="bui-lister-toolbar">
      <h1 class="bui-page-title">{{ pageTitle }}</h1>
      <div class="bui-lister-toolbar__tail">
        <TblTopBar
          v-model:hidden-columns="buiHiddenColumns"
          :columns="buiToggleColumns"
          :show-view-toggle="false"
          :compact-toolbar="isListingMobile"
        >
          <template #after-search>
            <slot name="toolbar-title-after" />
            <BulkActionsBar
              v-if="bulkActions.length || buiPrimaryActionResolved"
              :primary="buiPrimaryActionResolved"
              :secondary-actions="bulkActions"
              :secondary-disabled="bulkDisabled"
              @primary="onBuiPrimaryAction"
              @action="onBulkAction"
            />
          </template>
        </TblTopBar>
      </div>
    </div>

    <!--
      @click.stop: the overlay is teleported outside the lister's root (which
      stops propagation), and a click reaching document.body clears the list
      selection (useListSelection's clearSelectionTriggerElement).
    -->
    <Teleport to="body">
      <div v-if="confirmDialogOpen" class="bui-tbl-confirm-overlay" role="dialog" aria-modal="true" @click.stop>
        <div class="bui-tbl-confirm-card">
          <h3 class="title">Warning</h3>
          <component
            :is="ConfirmFormComponent"
            v-if="ConfirmFormComponent"
            :itemsIds="confirmItemsIds"
            @cancel="closeConfirmDialog"
            @success="onConfirmSuccess"
            @fail="onConfirmFail"
          />
        </div>
      </div>
    </Teleport>

    <!--
      Per Figma `bui-media` -> `tblDetailCount` (1335:8007), the
      "X items | N per page" strip lives ABOVE the table — not in the
      bottom bar. Bottom bar is pagination only.
    -->
    <TblDetailCount
      class="bui-lister__detail"
      :count="buiTotalItems"
      :label="buiEntityItemName"
      :plural-label="buiEntityItemsNamePlural"
      :loading="buiCountLoading"
      :limit="buiLimit"
      :limit-options="buiLimitOptions"
      @update:limit="onBuiLimitChange"
    />

    <div class="bui-tbl-scroll">
      <BuiTbl
        :columns="resolvedBuiColumns"
        :items="filteredListItems"
        :sort-key="(sortBy as any).fieldName"
        :sort-dir="(sortBy as any).isDescending ? 'desc' : 'asc'"
        :selected-ids="selectedIdsSet"
        :loading="!!isLoading"
        :error="buiErrorMessage"
        @sort="onBuiSort"
        @select-row="onBuiSelectRow"
        @select-all="onBuiSelectAll"
        @row-click="onBuiRowClick"
        @retry="onBuiRetry"
      />
    </div>

    <TblBottomBar />
  </div>

  <!-- Legacy chrome (preserved for any caller that hasn't migrated yet). -->
  <div v-else class="container">
    <FiltersPane />
    <div class="list-section-container">
      <!-- 
        Note: Stop propagation modifier is important for not unnecessarily triggering items deselct event — N2FCUcEt
        TODO: to think if there's a cleaner way to hand unnecessarily items deselect trigger — N2FCE8BF
      -->
      <ListTopBar @click.stop="" />
      <ItemsList :singleSelect="singleSelect" :selectMode="selectMode" :disableTableScroll="disableTableScroll" />
      <ListBottomBar />
    </div>
  </div>
</template>

<script lang="ts" setup>
import ListTopBar from './ListTopBar/ListTopBar.vue';
import ItemsList from './ItemsList/ItemsList.vue';
import GenericTableListItem from './ItemsList/TableView/GenericTableListItem.vue';
import ListBottomBar from './ListBottomBar/ListBottomBar.vue';
import FiltersPane from './FiltersPane/FiltersPane.vue';
import { computed, inject, provide, ref, shallowRef } from 'vue';
import { PhPlus } from '@phosphor-icons/vue';
import { useItemsListHelpers } from "./composables/itemsListHelpers/itemsListHelpers";
import { log } from '@/helpers/utils';
import useUser from '@/composables/user';
import TblTopBar from '@/components/bui/organisms/TblTopBar/TblTopBar.vue';
import TblBottomBar from '@/components/bui/organisms/TblBottomBar/TblBottomBar.vue';
import TblDetailCount from '@/components/bui/molecules/TblDetailCount/TblDetailCount.vue';
import BuiTbl from '@/components/bui/organisms/Tbl/Tbl.vue';
import type { BuiTblColumn } from '@/components/bui/organisms/Tbl/types';
import BulkActionsBar, {
  type SecondaryAction,
} from '@/components/bui/molecules/BulkActionsBar/BulkActionsBar.vue';
import DeleteItemConfirmationForm from '@/components/ListingPages/ItemsLister/ItemsList/ItemActionsButtons/DeleteItem/DeleteItemConfirmationForm/DeleteItemConfirmationForm.vue';
import CopyItemConfirmationForm from '@/components/ListingPages/ItemsLister/ItemsList/ItemActionsButtons/CopyItem/CopyItemConfirmationForm/CopyItemConfirmationForm.vue';
import useListSelection from './ItemsList/composables/listSelection';
import useIsMobile from '@/composables/utils/isMobile';

const user = useUser();
const isListingMobile = useIsMobile();

const props = defineProps<{
  filtersDisplayConfig?: any,
  resultsManipulationCallback?: any,
  apiPathsConfig?: any,
  TableViewItem?: any,
  itemPickDragMode?: any,
  itemPickDragCloneCallback?: Function,
  itemDragDropPlaceholderComponent?: any,
  availableActionsConfig?: any
  availableListViewsConfig?: any,
  singleSelect? :boolean,
  selectMode?:boolean,
  areaAccessName?: string,
  MetaDataOptionsComponent?: any,
  isEnableAllLimitOption?: boolean,
  itemsListHelpers?: any
  columnsToHide?:string[],
  disableTableScroll?: boolean,
  /** Switches the lister to render the new BUI table chrome. */
  useBuiChrome?: boolean,
  /** Column descriptors consumed by the BUI table organism. */
  buiColumns?: BuiTblColumn[],
  /** Optional bottom-bar primary action injected by BuiEntityListingPage. */
  buiPrimaryAction?: { label: string; icon?: any } | null,
}>()

const emit = defineEmits<{ primaryAction: [] }>()

const {
  filtersDisplayConfig,
  isEnableAllLimitOption,
  resultsManipulationCallback,
  apiPathsConfig,
  itemPickDragMode,
  itemPickDragCloneCallback,
  itemDragDropPlaceholderComponent,
  availableActionsConfig,
  TableViewItem,
  singleSelect,   
  selectMode,
  // TODO: think about renaming later to singleSelectMode
  availableListViewsConfig,
  areaAccessName,
  MetaDataOptionsComponent,
  columnsToHide
} = props;
const simpleEntityListingPageConfig = inject('simpleEntityListingPageConfig', {}) as any

const listerConfig = {
  itemPickDragMode,
  itemPickDragCloneCallback,
  itemDragDropPlaceholderComponent,
  availableActionsConfig,
  singleSelect,
  selectMode,
  availableListViewsConfig,
  areaAccess: {
    name: areaAccessName,
    hasReadAccess: user.hasReadAccessToArea(areaAccessName),
    hasWriteAccess: user.hasWriteAccessToArea(areaAccessName),
    hasCreateAccess: user.hasCreateAccessToArea(areaAccessName),
    hasDeleteAccess: user.hasDeleteAccessToArea(areaAccessName),
  },
  MetaDataOptionsComponent
}

defineExpose({
  areaAccessName
})

log(itemPickDragMode, {ns: 'ItemsLister', color: 'blue'});

provide('listerConfig', listerConfig)

const { 
  entityItemsNamePlural,
  entityItemName
} = simpleEntityListingPageConfig;

const itemsListHelpers = props.itemsListHelpers ||
  useItemsListHelpers({
    apiPathsConfig,
    resultsManipulationCallback,
    filtersDisplayConfig,
    isEnableAllLimitOption,
    entityItemsNamePlural,
    entityItemName,
    columnsToHide
  });

provide('itemsListHelpers', itemsListHelpers)
  // TODO: think if it's better to use "Symbol" instead of string for the "provide key" — N2QHle20
// Fall back to the generic column renderer when a lister doesn't supply its
// own row component (the select-from-lister dialogs). Without this the legacy
// table renders empty data cells, leaving the picker unreadable.
provide('TableViewItem', TableViewItem || GenericTableListItem)

const { disableTableScroll } = props;

// ---------------------------------------------------------------------------
// BUI chrome bridge
// ---------------------------------------------------------------------------
const useBuiChrome = computed(() => !!props.useBuiChrome);

const buiHiddenColumns = ref<string[]>([]);
const resolvedBuiColumns = computed<BuiTblColumn[]>(() => {
  const cols = (props.buiColumns ?? []) as BuiTblColumn[];
  if (!buiHiddenColumns.value.length) return cols.filter((c) => !c.hidden);
  const hidden = new Set(buiHiddenColumns.value);
  return cols.filter((c) => !c.hidden && !hidden.has(c.key));
});

const buiToggleColumns = computed(() =>
  (props.buiColumns ?? [])
    .filter((c: BuiTblColumn) => !!c.label) // skip checkbox-only columns
    .map((c: BuiTblColumn) => ({ key: c.key, label: c.label as string })),
);

// Local destructures used by the BUI bridge templates.
const filteredListItems = itemsListHelpers.filteredListItems;
const sortBy = itemsListHelpers.sortBy;
const isLoading = itemsListHelpers.isLoading;

const {
  selectedItemsIds,
  addItemToSelection,
  removeItemFromSelection,
  clearItemsSelection,
  addMultipleItemsToSelection,
  isUserHasCreateAccessToAllSelectedItems,
  isUserHasDeleteAccessToAllSelectedItems,
} = useListSelection({
  clearSelectionTriggerElement: typeof document !== 'undefined' ? document.body : null,
});

const selectedIdsSet = computed<Set<number | string>>(() => new Set(selectedItemsIds));

function onBuiSort({ column, nextDir }: { column: BuiTblColumn; nextDir: 'asc' | 'desc' }) {
  const key = column.sortKey || column.key;
  if (sortBy && typeof sortBy === 'object') {
    (sortBy as any).fieldName = key;
    (sortBy as any).isDescending = nextDir === 'desc';
  }
}

function onBuiSelectRow({ item, selected }: { item: any; selected: boolean }) {
  const id = item?.id;
  if (id == null) return;
  if (selected) {
    addItemToSelection(id, { afterSelectionActionArguments: { item } });
  } else {
    removeItemFromSelection(id, { afterSelectionActionArguments: { item } });
  }
}

function onBuiSelectAll({ selected }: { selected: boolean }) {
  if (selected) {
    const items = (filteredListItems.value || []).filter((it: any) => !selectedIdsSet.value.has(it.id));
    addMultipleItemsToSelection(items, { itemIdGetterCallback: (it: any) => it.id });
  } else {
    clearItemsSelection();
  }
}

function onBuiRowClick(_payload: { event: MouseEvent; item: any; index: number }) {
  // Reserved for future row-level interactions (e.g. expand). The cell-level
  // selection is already handled by the row checkbox.
}

const buiPrimaryActionResolved = computed(() => {
  if (props.buiPrimaryAction) return props.buiPrimaryAction;
  const n = itemsListHelpers?.entityItemName as string | undefined;
  if (!n) return null;
  return { label: `Add ${n}`, icon: PhPlus };
});

function onBuiPrimaryAction() {
  emit('primaryAction');
}

const pageTitle = computed(
  () =>
    (simpleEntityListingPageConfig?.entityItemsNamePlural as string | undefined) ??
    (itemsListHelpers?.entityItemsNamePlural as string | undefined) ??
    '',
);

const availableActions = computed(() => listerConfig?.availableActionsConfig ?? {});

// Edit is deliberately NOT a bulk action: single-item edit already lives on
// each row (TblRowActions, routed per entity via apiPathsConfig.edit), and
// editing N items at once is meaningless. The dropdown only carries true
// bulk operations, each behind a confirmation dialog.
const bulkActions = computed<SecondaryAction[]>(() => {
  const out: SecondaryAction[] = [];
  if (availableActions.value.copy) {
    out.push({
      value: 'copy',
      label: 'Copy',
      disabled: !listerConfig?.areaAccess?.hasCreateAccess || !isUserHasCreateAccessToAllSelectedItems.value,
    });
  }
  if (availableActions.value.delete) {
    out.push({
      value: 'delete',
      label: 'Delete',
      disabled: !listerConfig?.areaAccess?.hasDeleteAccess || !isUserHasDeleteAccessToAllSelectedItems.value,
    });
  }
  return out;
});

const bulkDisabled = computed(() => selectedItemsIds.length === 0);

const confirmDialogOpen = ref(false);
const ConfirmFormComponent = shallowRef<any>(null);
// Snapshot of the selection taken when the confirm dialog opens, so the
// operation is unaffected by the selection being cleared (e.g. by the
// body-click deselect handler) while the dialog is up.
const confirmItemsIds = ref<Array<number | string>>([]);

function openConfirm(component: any) {
  confirmItemsIds.value = [...selectedItemsIds];
  ConfirmFormComponent.value = component;
  confirmDialogOpen.value = true;
}

function closeConfirmDialog() {
  confirmDialogOpen.value = false;
  ConfirmFormComponent.value = null;
  confirmItemsIds.value = [];
}

function onConfirmSuccess() {
  closeConfirmDialog();
  clearItemsSelection();
  itemsListHelpers?.reloadList?.();
}

function onConfirmFail() {
  // Keep the dialog open so the error message stays visible. The API deletes
  // items one by one and keeps going on per-item failures, so reload to
  // reflect the items that were removed before the failure.
  itemsListHelpers?.reloadList?.();
}

function onBulkAction(value: string) {
  if (selectedItemsIds.length === 0) return;
  switch (value) {
    case 'copy':
      openConfirm(CopyItemConfirmationForm);
      break;
    case 'delete':
      openConfirm(DeleteItemConfirmationForm);
      break;
  }
}

// ---------------------------------------------------------------------------
// Detail-count strip (above the table) — Figma `tblDetailCount` (1335:8007)
// ---------------------------------------------------------------------------
const buiTotalItems = computed<number>(() => itemsListHelpers?.totalItems?.value ?? 0);
const buiCountLoading = computed<boolean>(
  () => !!itemsListHelpers?.isLoading?.value && !buiTotalItems.value,
);
const buiEntityItemName = computed<string>(() => {
  const n = itemsListHelpers?.entityItemName as string | undefined;
  return n ? n.toLowerCase() : 'item';
});
const buiEntityItemsNamePlural = computed<string>(() => {
  const n = itemsListHelpers?.entityItemsNamePlural as string | undefined;
  return n ? n.toLowerCase() : 'items';
});
const buiErrorMessage = computed<string>(() => {
  if (!itemsListHelpers?.error?.value) return '';
  return `Something went wrong while loading ${buiEntityItemsNamePlural.value}.`;
});

function onBuiRetry() {
  itemsListHelpers?.reloadList?.();
}

const buiLimit = computed<number | string>(() => itemsListHelpers?.limit?.value ?? 10);
const buiLimitOptions = computed<Array<number | { label: string; value: number }>>(() => {
  const opts: Array<number | { label: string; value: number }> = [10, 25, 50, 100];
  if (itemsListHelpers?.isEnableAllLimitOption) {
    opts.push({ label: 'All', value: 0 });
  }
  return opts;
});
function onBuiLimitChange(v: number | string) {
  if (itemsListHelpers?.limit) itemsListHelpers.limit.value = v;
}
</script>

<style lang="scss" scoped>
.container {
  /* width: 90%; */
  /* min-width: 500px; */
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-direction: row;

  @include x-large-devices-down {
    flex-direction: column;
  }
}

.list-section-container {
  max-width: 65rem;
  width: 100%;
  padding: 0 0 2rem 2rem;

  @include x-large-devices-down {
    padding: 2rem 0;
  }
}

/* BUI bridge layout: matches Figma `bui-media` (1335:7569). */
.bui-lister {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bui-lister-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  width: 100%;
}

.bui-page-title {
  flex-shrink: 0;
}

.bui-lister-toolbar__tail {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .bui-lister {
    gap: 6px;
  }

  .bui-page-title {
    font-size: 1.05rem;
    line-height: 1.2;
    font-weight: 700;
    margin: 0;
    letter-spacing: 0.01em;
  }

  .bui-lister-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .bui-lister-toolbar__tail {
    flex: none;
    width: 100%;
    justify-content: stretch;

    :deep(.bui-bulk-actions) {
      width: 100%;
      justify-content: flex-end;
    }
  }

  .bui-lister__detail {
    margin-bottom: -6px;
  }
}

.bui-tbl-scroll {
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.bui-lister__detail {
  /* The detail-count strip should anchor visually to the table card; the
     8px gap (24px gap minus the 8px we add back via margin) keeps it
     close per Figma which has the strip ~14px above the card. */
  margin-bottom: -8px;
}
</style>

<style lang="scss">
.bui-tbl-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.bui-tbl-confirm-card {
  background: #ffffff;
  color: #25294a;
  border-radius: 16px;
  padding: 24px;
  min-width: 320px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.32);

  .title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 12px 0;
  }
}
</style>